import styled, { css } from 'styled-components'
import { Spacings, Text, Devices } from '@styles'

export const SectionCard = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
	padding: ${Spacings.SPACING_4B};
	background: ${({ theme }) => theme.colors.surface.card};
	border-radius: ${({ theme }) => theme.radius.lg};
	box-shadow: ${({ theme }) => theme.shadows.sm};

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_5B};
	}
`

// Header row of a section: icon + title on the left, actions on the right.
export const SectionHead = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	width: 100%;
`

export const SectionTitle = styled.h3`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	margin: 0;
	font-size: ${Text.LARGE};
	font-weight: ${Text.BOLD_700};
	color: ${({ theme }) => theme.colors.text.primary};

	svg {
		font-size: ${Text.X_LARGE};
		color: ${({ theme }) => theme.colors.brand.primary};
	}
`

export const SectionActions = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
`

// Pills stacked one below the other, e.g. an oracle id over its code.
export const PillStack = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-end;
	gap: ${Spacings.SPACING_1B};
`

// Responsive card grid: 1 column on mobile, N (default 2) on tablet+.
export const CardGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: ${Spacings.SPACING_4B};

	@media ${Devices.tablet} {
		grid-template-columns: repeat(${({ $columns }) => $columns || 2}, 1fr);
	}
`

export const NoDataText = styled.p`
	margin: 0;
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.colors.text.secondary};
`

/* ─── Label / value field grid (detail cards) ─────────────────────────── */

export const FieldGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: ${Spacings.SPACING_4B} ${Spacings.SPACING_3B};

	@media ${Devices.tablet} {
		grid-template-columns: repeat(${({ $columns }) => $columns || 3}, 1fr);
	}
`

export const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1B};
	min-width: 0;
`

export const FieldLabel = styled.span`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_1B};
	font-size: ${Text.BASE_SMALL};
	font-weight: ${Text.BOLD_400};
	color: ${({ theme }) => theme.colors.text.secondary};
`

export const FieldValue = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.colors.text.primary};
	word-break: break-word;
`

// Field grid that stacks below laptop and splits into N equal columns on laptop+
// (it sits in a half-width card, so tablet is too narrow for 3 columns).
// minmax(0, 1fr) keeps long values from forcing horizontal overflow.
export const StackedFieldGrid = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	gap: ${Spacings.SPACING_4B} ${Spacings.SPACING_3B};

	@media ${Devices.laptop} {
		grid-template-columns: repeat(${({ $columns }) => $columns || 2}, minmax(0, 1fr));
	}
`

// Long list values clamp to 6 lines; the full text is shown in a tooltip.
export const ClampedFieldValue = styled(FieldValue)`
	display: -webkit-box;
	-webkit-line-clamp: 6;
	-webkit-box-orient: vertical;
	overflow: hidden;
`

/* ─── Tabs inside a card header (e.g. MBook Details / Company Info) ────── */

export const CardTabs = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_4B};
`

export const CardTab = styled.button`
	background: none;
	border: none;
	padding: 0 0 ${Spacings.SPACING_1B};
	font-family: ${({ theme }) => theme.fontFamily?.inter || "'Inter', sans-serif"};
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_500};
	cursor: pointer;
	color: ${({ theme, $active }) => ($active ? theme.colors.brand.primary : theme.colors.text.secondary)};
	border-bottom: 2px solid ${({ theme, $active }) => ($active ? theme.colors.brand.primary : 'transparent')};
	transition: color 0.2s ease-in-out, border-color 0.2s ease-in-out;
`

/* ─── Segmented ON/OFF toggle ──────────────────────────────────────────── */

export const Segment = styled.div`
	display: inline-flex;
	align-items: center;
	gap: ${Spacings.SPACING_1};
	width: fit-content;
	padding: ${Spacings.SPACING_1};
	background: ${({ theme }) => theme.colors.surface.sunken};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.sm};
`

export const SegmentOption = styled.button`
	border: none;
	cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
	padding: ${Spacings.SPACING_1} ${Spacings.SPACING_3B};
	border-radius: ${({ theme }) => theme.radius.xs};
	font-family: ${({ theme }) => theme.fontFamily?.inter || "'Inter', sans-serif"};
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_700};
	transition: all 0.2s ease-in-out;
	background: ${({ theme, $active }) => ($active ? theme.colors.brand.primary : 'transparent')};
	color: ${({ theme, $active }) => ($active ? theme.colors.text.inverse : theme.colors.text.secondary)};
`

/* ─── Page / tab layout ────────────────────────────────────────────────── */

// Wraps the routed tab content; adds breathing room below the top tab bar.
export const PageContent = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-height: 0;
	margin-top: ${Spacings.SPACING_3B};
	font-family: ${({ theme }) => theme.fontFamily?.inter || "'Inter', sans-serif"};

	/* Force every descendant (incl. buttons/inputs that reset it) onto Inter. */
	* {
		font-family: inherit;
	}

	@media ${Devices.tablet} {
		margin-top: ${Spacings.SPACING_4B};
	}
`

// Top row holding the main tabs on the left and contextual actions on the right.
export const TabBarRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_3B};
	width: 100%;
	font-family: ${({ theme }) => theme.fontFamily?.inter || "'Inter', sans-serif"};

	* {
		font-family: inherit;
	}
`

export const RightActions = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_5B};
	margin-left: auto;
`

// Sidebar (vertical tab rail) + content column.
export const TabBody = styled.div`
	display: flex;
	align-items: flex-start;
	flex-grow: 1;
	gap: ${Spacings.SPACING_4B};

	@media ${Devices.tablet} {
		gap: ${Spacings.SPACING_4B};
	}
`

// Right-hand content column that flows with the page (lets the sticky rail stick).
export const ContentPanel = styled.section`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-width: 0;
	gap: ${Spacings.SPACING_4B};
`

/* ─── Sticky vertical tab rail ─────────────────────────────────────────── */

export const VerticalTabsWrapper = styled.nav`
	position: sticky;
	top: ${({ $top }) => $top || Spacings.SPACING_4B};
	align-self: flex-start;
	display: flex;
	flex-direction: column;
	align-items: center;
	flex-shrink: 0;
	gap: ${Spacings.SPACING_2};
	width: ${({ $width }) => $width || 'fit-content'};
`

// Vertical tab box — local copy (previously imported from FinalReport/styles) so the
// OrganisationV2 folder is self-contained. Used by components/VerticalTabs.jsx.
export const BoxWapper = styled.div`
	font-size: ${Text.EXTRA_SMALL};
	display: flex;
	flex-direction: row;
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_1B};
	gap: ${Spacings.SPACING_5};
	cursor: pointer;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	& > p {
		width: 3rem;
		text-align: center;
	}
	@media ${Devices.mobile} {
		& > p {
			width: 4rem;
		}
		margin: 0 0 ${Spacings.SPACING_2B} 0;
		flex-direction: row;
	}
	@media ${Devices.tablet} {
		flex-direction: column;
	}
	border-radius: ${Spacings.SPACING_2B};
	box-shadow: 1px 1px ${({ theme }) => theme.palette.background.disabled};
	border: 1px solid ${({ theme }) => theme.palette.background.border};
	fill: ${({ theme }) => theme.palette.text.grey};
	color: ${({ theme }) => theme.palette.background.blueLight};
	background: ${({ theme }) => theme.palette.background.white};
	&: hover {
		background: ${({ theme }) => theme.palette.background.blueLight};
		& > svg {
			fill: ${({ theme }) => theme.palette.background.white};
		}
		& > p {
			color: ${({ theme }) => theme.palette.background.white};
		}
	}

	${({ isSelected }) => {
		if (isSelected) {
			return css`
				border: 1px solid ${({ theme }) => theme.palette.background.blueLight};
				background: ${({ theme }) => theme.palette.background.blueLight};
				& > p {
					color: ${({ theme }) => theme.palette.background.white};
				}
				& > svg {
					fill: ${({ theme }) => theme.palette.background.white};
				}
			`
		}
	}}
`

export const InputsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
`

export const DropdownWrapper = styled.div`
	position: relative;
`

export const PaymentTermsWrapper = styled.div`
	margin-top: ${Spacings.SPACING_6B};
`
